import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DialpadComponent } from './dialpad/dialpad.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { DtmfDialogComponent } from './dtmf-dialog/dtmf-dialog.component';
import { LoginComponent } from './login/login.component';
import {
  APOLLO_NAMED_OPTIONS,
  ApolloModule,
  NamedOptions,
} from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import {
  InMemoryCache,
  split,
  ApolloLink,
  Operation,
  FetchResult,
  Observable,
  NextLink,
} from '@apollo/client/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { HomeComponent } from './home/home.component';
import { CallHistoryComponent } from './call-history/call-history.component';
import { ContactsComponent } from './contacts/contacts.component';
import { TokenService } from './services/token.service';
import { AuthService } from './services/auth.service';
import { combineLatest, debounceTime, lastValueFrom } from 'rxjs';
import { SipService } from './services/sip.service';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ContactCardComponent } from './contact-card/contact-card.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarModule,
} from '@angular/material/snack-bar';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';

import { setContext } from '@apollo/client/link/context';
import { CallHistoryCallCardComponent } from './call-history-call-card/call-history-call-card.component';
import { CallHistoryVoicemailCardComponent } from './call-history-voicemail-card/call-history-voicemail-card.component';
import { StatusPanelComponent } from './status-panel/status-panel.component';
import { AppErrorHandler } from './Handlers/app-error.handler';
import { LoggingInterceptor } from './interceptors/logging.interceptor';
import { LogModule, LogService } from './services/log.service';
import { DeviceSettingsDialogComponent } from './device-settings-dialog/device-settings-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ContextSetter } from '@apollo/client/link/context';
import { CallQueueDialogComponent } from './call-queue-dialog/call-queue-dialog.component';
import { ErrorDialogComponent } from './shared/error-dialog/error-dialog.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { AudioUnlockService } from './services/audio-unlock.service';
import * as microsoftTeams from '@microsoft/teams-js';
import { ConferenceCallsComponent } from './shared/conference-calls/conference-calls.component';
import { MakeCallComponent } from './shared/make-call/make-call.component';
import { TransferCallComponent } from './shared/transfer-call/transfer-call.component';
import { ContactAvatarComponent } from './shared/contact-avatar/contact-avatar.component';
import { UserProfileDialogComponent } from './user-profile-dialog/user-profile-dialog.component';

export let logSubscription: (message: string) => void = () => {};

async function isRunningInTeams(): Promise<boolean> {
  try {
    await microsoftTeams?.app?.initialize();
    return true;
  } catch {
    return false;
  }
}

export function initializeApp(
  tokenService: TokenService,
  authService: AuthService,
  sipService: SipService,
  router: Router,
  logService: LogService,
  audioUnlockService: AudioUnlockService,
  dialog: MatDialog
): () => Promise<void> {
  return async () => {
    // Initialize the logSubscription function
    logSubscription = async (message: string) => {
      await logService.logInfo(message, LogModule.GRAPHQL);
    };

    // Check if the user is already authenticated
    const combinedTokenValidity$ = combineLatest([
      tokenService.isRefreshTokenValid$,
      tokenService.isEveportalJwtTokenValid$,
    ]);

    try {
      combinedTokenValidity$.subscribe(
        async ([isRefreshTokenValid, isEveportalJwtTokenValid]) => {
          if (isRefreshTokenValid && isEveportalJwtTokenValid) {
            const loginRefreshToken = tokenService.getLoginRefreshToken();
            const inTeams = await isRunningInTeams();

            const initialSip = async () => {
              await audioUnlockService.unlock();

              await sipService.initialize(loginRefreshToken);
              console.log('🔁 SIP initialized successfully!');
              console.log('🔁 Auto login successful!');
              router.navigate(['/home']);
            };
            if (!inTeams && !audioUnlockService.isUnlocked) {
              const ref = dialog.open(WelcomeComponent, {
                disableClose: true,
              });
              const ok = await ref.afterClosed().toPromise();
              if (ok) {
                await initialSip();
              }
            } else {
              await initialSip();
            }
          } else if (
            isRefreshTokenValid === true &&
            isEveportalJwtTokenValid === false
          ) {
            const refreshToken = tokenService.getRefreshToken()!;
            const response = await lastValueFrom(
              authService.performAuthenticatedFlow(refreshToken)
            );
            const loginRefreshToken = response.data?.loginRefreshToken ?? null;
            tokenService.setLoginRefreshToken(loginRefreshToken);
          } else if (
            isRefreshTokenValid === false &&
            isEveportalJwtTokenValid === false
          ) {
            console.log('🔁 No valid tokens found. Redirecting to login...');
            router.navigate(['/login']);
          }
        }
      );
    } catch (error: any) {
      console.error(error);
      logService.logError(error.message);
      router.navigate(['/login']);
      return;
    }
  };
}

export function getEveportalJwtHeader(
  tokenService: TokenService,
  operationName?: string,
  body?: any
): { [key: string]: string } | {} {
  // 🚫 Skip token for login mutation
  if (
    operationName === 'Login' ||
    /mutation\s+login/i.test(body?.query || '')
  ) {
    return {};
  }

  const token = tokenService.getEveportalJwtToken();

  if (tokenService.isEveportalJwtTokenValid() && token) {
    return { 'eveportal-jwt': token };
  }

  return {};
}

@NgModule({
  declarations: [
    AppComponent,
    DialpadComponent,
    DtmfDialogComponent,
    LoginComponent,
    HomeComponent,
    CallHistoryComponent,
    ContactsComponent,
    ContactCardComponent,
    CallHistoryCallCardComponent,
    CallHistoryVoicemailCardComponent,
    StatusPanelComponent,
    DeviceSettingsDialogComponent,
    CallQueueDialogComponent,
    ErrorDialogComponent,
    WelcomeComponent,
    ConferenceCallsComponent,
    MakeCallComponent,
    TransferCallComponent,
    ContactAvatarComponent,
    UserProfileDialogComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatDialogModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSelectModule,
    MatOptionModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    FontAwesomeModule,
    ScrollingModule,
    MatButtonToggleModule,
    ApolloModule,

    HttpClientModule,

    AppRoutingModule,
  ],
  providers: [
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 4000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        // optional: apply a global style
        // panelClass: ['app-snackbar']
      },
    },
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory: (
        httpLink: HttpLink,
        tokenService: TokenService
      ): NamedOptions => {
        // 🔁 Dynamically build HTTP URI per request
        const dynamicEvePortalUriLink = new ApolloLink((operation, forward) => {
          const uri = `${environment.config.system.evePortalApiUrl}/graphql`;
          operation.setContext({ uri });
          return forward(operation);
        });

        const dynamicPresenceVoiceUriLink = new ApolloLink(
          (operation, forward) => {
            const uri = `${environment.config.system.presenceVoiceUrl}/graphql`;
            operation.setContext({ uri });
            return forward(operation);
          }
        );

        // 🛡️ Add JWT headers per request
        const withAuthHeaders: ContextSetter = (operation, prevContext) => {
          const headers = getEveportalJwtHeader(
            tokenService,
            operation.operationName,
            operation.query?.loc?.source?.body
          );

          return {
            headers: {
              ...prevContext.headers,
              ...headers,
            },
          };
        };

        // 🔹 Default evePortal link
        const defaultLink = setContext(withAuthHeaders)
          .concat(dynamicEvePortalUriLink)
          .concat(
            httpLink.create({
              uri: `${environment.config.system.evePortalApiUrl}/graphql`,
            })
          );

        // 🔸 Presence HTTP link
        const presenceHttp = setContext(withAuthHeaders)
          .concat(dynamicPresenceVoiceUriLink)
          .concat(
            httpLink.create({
              uri: `${environment.config.system.presenceVoiceUrl}/graphql`,
            })
          );

        // 🔁 Create dynamic WebSocket link for Presence
        const createPresenceWsLink = (): WebSocketLink => {
          return new WebSocketLink({
            uri: `${environment.config.system.presenceVoiceUrl.replace(
              /^http/,
              'ws'
            )}/graphql`,
            options: {
              reconnect: true,
              lazy: true,
              connectionParams: async () => ({
                'eveportal-jwt':
                  (await tokenService.getEveportalJwtToken()) || '',
              }),
            },
          });
        };

        const presenceWs = createPresenceWsLink();

        // 📡 Subscription logging wrapper
        const logSubscriptionLink = new ApolloLink(
          (operation: Operation, forward: NextLink) => {
            const isSubscription = operation.query.definitions.some(
              (def) =>
                def.kind === 'OperationDefinition' &&
                def.operation === 'subscription'
            );

            const operationName =
              operation.operationName || 'UnnamedSubscription';
            const variables = operation.variables || {};
            const eveUserName = variables['eveUserName'] || '(unknown)';

            if (isSubscription) {
              const startTime = Date.now();
              const cleanedQuery = operation.query.loc?.source.body
                .trim()
                .replace(/\s+/g, ' ');

              (async () => {
                logSubscription(
                  `🛰️ [SUBSCRIBE] ${operationName}\n` +
                    `Started at: ${new Date(startTime).toISOString()}\n` +
                    `Variables: ${JSON.stringify(variables, null, 2)}\n` +
                    `Query: ${cleanedQuery}\n`
                );
              })();

              return new Observable<FetchResult>((observer) => {
                const sub = forward(operation).subscribe({
                  next: (result) => observer.next(result),
                  error: async (err) => {
                    const duration = Date.now() - startTime;
                    await logSubscription(
                      `❌ [SUB ERROR] ${operationName} for ${eveUserName}\n` +
                        `Duration: ${duration}ms\n` +
                        `Error: ${err.message}\n`
                    );
                    observer.error(err);
                  },
                  complete: async () => {
                    const duration = Date.now() - startTime;
                    await logSubscription(
                      `✅ [SUB COMPLETE] ${operationName} for ${eveUserName}\n` +
                        `Duration: ${duration}ms`
                    );
                    observer.complete();
                  },
                });

                return () => {
                  const duration = Date.now() - startTime;
                  logSubscription(
                    `🚪 [UNSUBSCRIBED] ${operationName} for ${eveUserName}\n` +
                      `Duration: ${duration}ms`
                  );
                  sub.unsubscribe();
                };
              });
            }

            return forward(operation);
          }
        );

        // 🔀 Combine HTTP + WS for presence client
        const presenceLink = split(
          ({ query }) => {
            const def = getMainDefinition(query);
            return (
              def.kind === 'OperationDefinition' &&
              def.operation === 'subscription'
            );
          },
          logSubscriptionLink.concat(presenceWs),
          presenceHttp
        );

        return {
          default: {
            cache: new InMemoryCache(),
            link: defaultLink,
          },
          presence: {
            cache: new InMemoryCache(),
            link: presenceLink,
          },
        };
      },
      deps: [HttpLink, TokenService],
    },

    // ⏳ Your other providers
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [
        TokenService,
        AuthService,
        SipService,
        Router,
        LogService,
        AudioUnlockService,
        MatDialog,
      ],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
    { provide: ErrorHandler, useClass: AppErrorHandler },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
